import React, { Component, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import ErrorWrapper from './ErrorWrapper';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack
        }
      }
    });
  }

  public render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div id="error-boundary">
          <ErrorWrapper title="Something went wrong">
            An unexpected error has occurred. Please try refreshing the page or contact support if the problem persists.
          </ErrorWrapper>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
